import { computed, ref } from "vue";
import { EIntegracoesAPIEnum } from "../../../../../@types/IntegracoesAPIEnum";
import { TIntegracaoCampoModel } from "../@types/IntegracaoCampoModel";
import { IDefaultHttpFinancasResponse } from "@/core/@types/types";
import useNormalizeFields from "./dynamicForm/handlers/useNormalizeFields/useNormalizeFields";
import useBuildValidationSchema from "./dynamicForm/handlers/useBuildValidationSchema/useBuildValidationSchema";
import useBuildInitialValues from "./dynamicForm/handlers/useBuildInitialValues/useBuildInitialValues";
import {
  CadastroDeIntegracaoGateway,
  TIntegracaoAction,
  TIntegracaoPorIdCampo,
  TObterIntegracaoPorIdResponse
} from "../services/CadastroDeIntegracaoGateway";

export type TObterCamposOptions = {
  queryParams?: Record<string, string>;
  preserveValues?: Record<string, unknown>;
};

export default function useCadastroIntegracao(
  gateway: CadastroDeIntegracaoGateway
) {
  const { groupFields, normalizeFields } = useNormalizeFields();

  const { buildSchema } = useBuildValidationSchema();
  const { buildInitialValues } = useBuildInitialValues();

  const isLoading = ref(false);
  const erro = ref<string | null>(null);
  const campos = ref<TIntegracaoCampoModel[]>([]);
  const submitAction = ref<TIntegracaoAction | null>(null);
  const editAction = ref<TIntegracaoAction | null>(null);
  const webserviceId = ref<string | null>(null);
  const apiHashEdicao = ref<EIntegracoesAPIEnum | null>(null);
  const ativoEdicao = ref<boolean | null>(null);
  const preservedValues = ref<Record<string, unknown>>({});
  let requestSequence = 0;

  const grupos = computed(() => groupFields(campos.value));
  const schema = computed(() => buildSchema(campos.value));
  const initialValues = computed(() => {
    const base = buildInitialValues(campos.value);
    const nomesCampos = new Set(campos.value.map((campo) => campo.nomeCampo));

    const preserved = Object.entries(preservedValues.value).reduce<
      Record<string, unknown>
    >((acc, [key, value]) => {
      if (nomesCampos.has(key)) acc[key] = value;

      return acc;
    }, {});

    return { ...base, ...preserved };
  });

  const extrairCamposDtoObterIntegracaoPorId = (
    response:
      | IDefaultHttpFinancasResponse<TObterIntegracaoPorIdResponse>
      | undefined
  ): TIntegracaoPorIdCampo[] => {
    const lista = response?.Result?.campos;

    if (!Array.isArray(lista) || lista.length === 0) {
      return [];
    }

    return Array.isArray(lista) ? lista : [];
  };

  const obterCampos = async (
    apiHash: EIntegracoesAPIEnum,
    options?: TObterCamposOptions
  ) => {
    const requestId = ++requestSequence;
    isLoading.value = true;
    erro.value = null;

    const visiveisAntes = new Set(
      campos.value
        .filter((campo) => campo.visivel)
        .map((campo) => campo.nomeCampo)
    );
    const candidatosAPreservar = Object.entries(
      options?.preserveValues ?? {}
    ).filter(([nomeCampo]) => visiveisAntes.has(nomeCampo));

    try {
      const response = await gateway.obterIntegracaoPorId(
        apiHash,
        options?.queryParams
      );
      if (requestId !== requestSequence) return;

      const dtos = extrairCamposDtoObterIntegracaoPorId(response);
      const previousFields = new Map(
        campos.value.map((campo) => [campo.nomeCampo, campo])
      );
      campos.value = normalizeFields(dtos).map((campo) => {
        const previous = previousFields.get(campo.nomeCampo);
        if (previous?.valorPersistido === undefined) return campo;

        return {
          ...campo,
          valorPersistido: previous.valorPersistido,
          hasPersistedValue: previous.hasPersistedValue
        };
      });
      if (!webserviceId.value)
        submitAction.value = response?.Result?.submitAction ?? null;

      const visiveisAgora = new Set(
        campos.value
          .filter((campo) => campo.visivel)
          .map((campo) => campo.nomeCampo)
      );
      preservedValues.value = candidatosAPreservar.reduce<
        Record<string, unknown>
      >((acc, [nomeCampo, valor]) => {
        if (visiveisAgora.has(nomeCampo)) acc[nomeCampo] = valor;

        return acc;
      }, {});
    } catch (e) {
      if (requestId !== requestSequence) return;
      console.error(e);
      erro.value = (e as Error).message;
      campos.value = [];
      submitAction.value = null;
    } finally {
      if (requestId === requestSequence) isLoading.value = false;
    }
  };

  const carregarEdicao = async (id: string) => {
    const requestId = ++requestSequence;
    isLoading.value = true;
    erro.value = null;
    preservedValues.value = {};

    try {
      const response = await gateway.getWebserviceForEdit(id);
      if (requestId !== requestSequence) return;

      const result = response?.Result;
      if (!response?.Ok || !result)
        throw new Error(
          response?.Error ?? "Não foi possível carregar a integração."
        );

      webserviceId.value = String(result._id ?? id);
      apiHashEdicao.value = result.api as EIntegracoesAPIEnum;
      ativoEdicao.value =
        typeof result.ativo === "boolean" ? result.ativo : null;
      campos.value = normalizeFields(result.campos ?? []);
      submitAction.value = result.submitAction ?? null;
      editAction.value = result.editAction ?? null;
    } catch (e) {
      if (requestId !== requestSequence) return;
      console.error(e);
      erro.value = (e as Error).message;
      campos.value = [];
      submitAction.value = null;
      editAction.value = null;
      webserviceId.value = null;
      apiHashEdicao.value = null;
      ativoEdicao.value = null;
    } finally {
      if (requestId === requestSequence) isLoading.value = false;
    }
  };

  const reset = () => {
    requestSequence += 1;
    isLoading.value = false;
    erro.value = null;
    campos.value = [];
    submitAction.value = null;
    editAction.value = null;
    webserviceId.value = null;
    apiHashEdicao.value = null;
    ativoEdicao.value = null;
    preservedValues.value = {};
  };

  return {
    isLoading,
    erro,
    campos,
    grupos,
    schema,
    initialValues,
    submitAction,
    editAction,
    webserviceId,
    apiHashEdicao,
    ativoEdicao,
    obterCampos,
    carregarEdicao,
    reset
  };
}
